import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCallback } from "react";

interface CachedUsersData {
  accessRequests: any[];
  profiles: any[];
}

export function useOptimizedUsersCache() {
  const queryClient = useQueryClient();

  // Ultra-fast cached query with 5-minute stale time
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["users", "optimized"],
    queryFn: async (): Promise<CachedUsersData> => {
      console.log("[Cache] Fast parallel fetch starting...");
      
      // Parallel queries with minimal data selection
      const [accessRequestsResult, profilesResult] = await Promise.allSettled([
        supabase
          .from('access_requests')
          .select('id,first_name,last_name,email,phone,company_name,license_number,message,status,created_at')
          .eq('status', 'pending')
          .order('created_at', { ascending: false })
          .limit(100), // Limit for performance
        supabase
          .from('user_profiles')
          .select('id,first_name,last_name,email,phone,company_name,license_number,role,is_approved,is_banned,created_at')
          .order('created_at', { ascending: false })
          .limit(500) // Limit for performance
      ]);

      let accessRequests: any[] = [];
      let profiles: any[] = [];

      if (accessRequestsResult.status === 'fulfilled' && !accessRequestsResult.value.error) {
        accessRequests = accessRequestsResult.value.data || [];
      }

      if (profilesResult.status === 'fulfilled' && !profilesResult.value.error) {
        profiles = profilesResult.value.data || [];
      }

      // Filter duplicates efficiently
      const existingEmails = new Set(profiles.map(p => p.email));
      const filteredRequests = accessRequests.filter(r => !existingEmails.has(r.email));

      console.log(`[Cache] Loaded ${filteredRequests.length} requests, ${profiles.length} profiles`);
      
      return {
        accessRequests: filteredRequests,
        profiles: profiles
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - keeps data fresh without constant refetching
    gcTime: 10 * 60 * 1000, // 10 minutes garbage collection
    refetchOnWindowFocus: false,
    refetchOnMount: true
  });

  // Instant data return for table
  const getUsersData = useCallback(() => {
    if (!data) return [];
    return [...data.accessRequests, ...data.profiles];
  }, [data]);

  // Background refresh without blocking UI
  const refreshData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["users", "optimized"] });
  }, [queryClient]);

  return {
    data: getUsersData(),
    isLoading,
    refreshData,
    refetch
  };
}